// studentapps/ProblemSolving.js
import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Lightbulb as LightbulbIcon,
  CorporateFare as CorporateIcon
} from '@mui/icons-material';

const ProblemSolving = () => {
  const [problems, setProblems] = useState([
    {
      id: 1,
      title: 'Algebra Equation',
      description: 'Solve for x: 2x + 5 = 15',
      subject: 'Mathematics',
      difficulty: 'Easy',
      status: 'solved',
      solution: 'x = 5',
      createdAt: '2024-01-10'
    },
    {
      id: 2,
      title: 'Physics Kinematics',
      description: 'Calculate velocity with given acceleration and time',
      subject: 'Physics',
      difficulty: 'Medium',
      status: 'pending',
      createdAt: '2024-01-11'
    },
    {
      id: 3,
      title: 'Chemical Reactions',
      description: 'Balance the chemical equation for combustion',
      subject: 'Chemistry',
      difficulty: 'Hard',
      status: 'pending',
      createdAt: '2024-01-12'
    }
  ]);

  const [newProblem, setNewProblem] = useState({
    title: '',
    description: '',
    subject: '',
    difficulty: 'Medium'
  });

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedProblem, setSelectedProblem] = useState(null);

  const handleAddProblem = () => {
    if (newProblem.title && newProblem.description) {
      const problem = {
        id: problems.length + 1,
        ...newProblem,
        status: 'pending',
        createdAt: new Date().toISOString().split('T')[0]
      };
      setProblems([...problems, problem]);
      setNewProblem({ title: '', description: '', subject: '', difficulty: 'Medium' });
      setOpenDialog(false);
    }
  };

  const handleSolveProblem = (problemId) => {
    setProblems(problems.map(problem => 
      problem.id === problemId 
        ? { ...problem, status: 'solved', solution: 'Solution added successfully!' }
        : problem
    ));
  };

  const handleDeleteProblem = (problemId) => {
    setProblems(problems.filter(problem => problem.id !== problemId));
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case 'Easy': return 'success';
      case 'Medium': return 'warning';
      case 'Hard': return 'error';
      default: return 'default';
    }
  };

  const solvedCount = problems.filter(p => p.status === 'solved').length;
  const pendingCount = problems.filter(p => p.status === 'pending').length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        <Box>
          <Typography variant="h4" fontWeight="700" gutterBottom>
            Problem Solving
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Track and solve academic challenges
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setOpenDialog(true)}
          size="large"
        >
          Add Problem
        </Button>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5" fontWeight="600">
                  My Problems
                </Typography>
                <Chip 
                  label="Powered by Zolang Nexora" 
                  size="small" 
                  color="primary" 
                  variant="outlined"
                  icon={<CorporateIcon />}
                />
              </Box>
              
              <Grid container spacing={2}>
                {problems.map((problem) => (
                  <Grid item xs={12} key={problem.id}>
                    <Paper sx={{ p: 3, borderRadius: 3 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Box sx={{ flexGrow: 1 }}>
                          <Typography variant="h6" gutterBottom>
                            {problem.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {problem.description}
                          </Typography>
                          
                          <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
                            <Chip label={problem.subject} size="small" color="primary" />
                            <Chip 
                              label={problem.difficulty} 
                              size="small"
                              color={getDifficultyColor(problem.difficulty)}
                            />
                            <Chip 
                              label={problem.status} 
                              size="small"
                              color={problem.status === 'solved' ? 'success' : 'default'}
                              variant="outlined"
                            />
                            <Chip 
                              label={problem.createdAt} 
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          
                          {problem.solution && (
                            <Paper variant="outlined" sx={{ p: 2, bgcolor: 'success.light', borderRadius: 2 }}>
                              <Typography variant="subtitle2" gutterBottom color="success.dark">
                                ✅ Solution:
                              </Typography>
                              <Typography variant="body2" color="success.dark">
                                {problem.solution}
                              </Typography>
                            </Paper>
                          )}
                        </Box>
                        
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                          <IconButton 
                            color="primary"
                            onClick={() => handleSolveProblem(problem.id)}
                            disabled={problem.status === 'solved'}
                            sx={{ 
                              background: problem.status === 'solved' ? 'success.light' : 'primary.light',
                              '&:hover': { background: problem.status === 'solved' ? 'success.main' : 'primary.main' }
                            }}
                          >
                            <LightbulbIcon />
                          </IconButton>
                          <IconButton 
                            color="error"
                            onClick={() => handleDeleteProblem(problem.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Problem Statistics
              </Typography>
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography variant="h3" color="primary" fontWeight="700">
                  {problems.length}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Total Problems
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 2 }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="success.main" fontWeight="700">
                    {solvedCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Solved
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h4" color="warning.main" fontWeight="700">
                    {pendingCount}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Pending
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Quick Tips
              </Typography>
              <List dense>
                {[
                  'Break complex problems into smaller parts',
                  'Draw diagrams to visualize the problem',
                  'Review similar solved examples',
                  'Take breaks when stuck',
                  'Explain the problem to someone else'
                ].map((tip, index) => (
                  <ListItem key={index}>
                    <ListItemText primary={`${index + 1}. ${tip}`} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Problem Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h5" fontWeight="600">
            Add New Problem
          </Typography>
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Problem Title"
            fullWidth
            variant="outlined"
            value={newProblem.title}
            onChange={(e) => setNewProblem({...newProblem, title: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Problem Description"
            fullWidth
            multiline
            rows={3}
            variant="outlined"
            value={newProblem.description}
            onChange={(e) => setNewProblem({...newProblem, description: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Subject"
            fullWidth
            variant="outlined"
            value={newProblem.subject}
            onChange={(e) => setNewProblem({...newProblem, subject: e.target.value})}
            sx={{ mb: 2 }}
          />
          <TextField
            select
            margin="dense"
            label="Difficulty"
            fullWidth
            variant="outlined"
            value={newProblem.difficulty}
            onChange={(e) => setNewProblem({...newProblem, difficulty: e.target.value})}
          >
            <MenuItem value="Easy">Easy</MenuItem>
            <MenuItem value="Medium">Medium</MenuItem>
            <MenuItem value="Hard">Hard</MenuItem>
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button onClick={handleAddProblem} variant="contained">
            Add Problem
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProblemSolving;